import useLocalStorage from "use-local-storage";
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import {
  Box,
  Button,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowModel, GridToolbarContainer } from "@mui/x-data-grid";

import {
  ButtonTrashRow,
  COMPONENT_CUSTOM_MAXHEIGHT,
  CONDITION_DEFINITIONS,
  CONDITION_OWNERS,
  conditionOwnersDescToIdMap,
  distinctGrades,
  fetchDeleteStudentConditions,
  fetchDeleteTeacherConditions,
  fetchGetFailed,
  fetchGetStudentConditionsAll,
  fetchGetStudentsAll,
  fetchGetTeacherConditionsAll,
  fetchPostStudentConditions,
  fetchPostTeacherConditions,
  fetchPutStudentCondition,
  fetchPutTeacherCondition,
  GRADES,
  PSConflictIcon,
  psLocalStorageCond,
  PSNoRowsOverlayCompact,
  PSTooltip,
  SetSnackType,
  StudentConditionsNoNulls,
  StudentsNoNulls,
  TeacherConditionsNoNulls,
  TeachersNoNulls,
  VFailedNoNulls,
} from "../../utils";
import { teacherGrToFieldName } from "../teachers";
import { AddStudentConditionDialog, AddTeacherConditionDialog } from ".";

const DoDeleteStuCond = () => {
  return useCallback(
    (sc: StudentConditionsNoNulls) =>
      new Promise<Object>(async (resolve, reject) => {
        try {
          const result = (await fetchDeleteStudentConditions([sc])) as Object;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoDeleteTchCond = () => {
  return useCallback(
    (tc: TeacherConditionsNoNulls) =>
      new Promise<Object>(async (resolve, reject) => {
        try {
          const result = (await fetchDeleteTeacherConditions([tc])) as Object;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPostStuCond = () => {
  return useCallback(
    (sc: StudentConditionsNoNulls) =>
      new Promise<Object>(async (resolve, reject) => {
        try {
          const result = (await fetchPostStudentConditions([sc])) as Object;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPostTchCond = () => {
  return useCallback(
    (tc: TeacherConditionsNoNulls) =>
      new Promise<Object>(async (resolve, reject) => {
        try {
          const result = (await fetchPostTeacherConditions([tc])) as Object;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPutStuCond = () => {
  return useCallback(
    (sc: StudentConditionsNoNulls) =>
      new Promise<StudentConditionsNoNulls>(async (resolve, reject) => {
        if (!sc.source_id) {
          reject(new Error("Error while saving: source can't be empty."));
        } else if (!sc.target_id) {
          reject(new Error("Error while saving: target can't be empty."));
        } else {
          try {
            const updatedDoc = (await fetchPutStudentCondition(sc)) as StudentConditionsNoNulls;
            resolve(updatedDoc);
          } catch (err: any) {
            reject(err);
          }
        }
      }),
    [],
  );
};

const DoPutTchCond = () => {
  return useCallback(
    (tc: TeacherConditionsNoNulls) =>
      new Promise<TeacherConditionsNoNulls>(async (resolve, reject) => {
        if (!tc.source_id) {
          reject(new Error("Error while saving: source can't be empty."));
        } else if (!tc.target_id) {
          reject(new Error("Error while saving: target can't be empty."));
        } else {
          try {
            const updatedDoc = (await fetchPutTeacherCondition(tc)) as TeacherConditionsNoNulls;
            resolve(updatedDoc);
          } catch (err: any) {
            reject(err);
          }
        }
      }),
    [],
  );
};

export const Conditions = (props: {
  clearTarget: () => void;
  disconnect: () => void;
  setSnack: SetSnackType;
  targetId: number | undefined;
  teachers: TeachersNoNulls[];
}) => {
  const [storedSelectedStudentId, setStoredSelectedStudentId] = useLocalStorage(psLocalStorageCond, 0);

  const [rows, setRows] = useState(Array<StudentsNoNulls>());
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState({} as StudentsNoNulls);
  const [selectedRowStuConds, setSelectedRowStuConds] = useState(Array<StudentConditionsNoNulls>());
  const [selectedRowTchConds, setSelectedRowTchConds] = useState(Array<TeacherConditionsNoNulls>());
  const [rowsFailed, setRowsFailed] = useState(Array<VFailedNoNulls>());

  const [studentConditionsRows, setStudentConditionsRows] = useState(Array<StudentConditionsNoNulls>());
  const [studentConditionsRowsLoading, setStudentConditionsRowsLoading] = useState(false);
  const [addStudentConditionDialogOpen, setAddStudentConditionDialogOpen] = useState(false);

  const [teacherConditionsRows, setTeacherConditionsRows] = useState(Array<TeacherConditionsNoNulls>());
  const [teacherConditionsRowsLoading, setTeacherConditionsRowsLoading] = useState(false);
  const [addTeacherConditionDialogOpen, setAddTeacherConditionDialogOpen] = useState(false);

  const [actionDeleteStuCondVisible, setActionDeleteStuCondVisible] = useState(false);
  const [actionDeleteTchCondVisible, setActionDeleteTchCondVisible] = useState(false);

  const getStudents = async () => {
    setRowsLoading(true);
    const resultStudents = await fetchGetStudentsAll();
    if (Object.keys(resultStudents).includes("error")) {
      props.disconnect();
      props.setSnack((resultStudents as any).error?.toString(), "error");
    } else {
      setRows(
        (resultStudents as StudentsNoNulls[]).toSorted(
          (a, b) => GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade) || a.last.localeCompare(b.last),
        ),
      );
    }
    setRowsLoading(false);
  };

  const getStuConds = async () => {
    setStudentConditionsRowsLoading(true);
    const resultStuConds = await fetchGetStudentConditionsAll();
    if (Object.keys(resultStuConds).includes("error")) {
      props.disconnect();
      props.setSnack((resultStuConds as any).error?.toString(), "error");
    } else {
      setStudentConditionsRows(resultStuConds as StudentConditionsNoNulls[]);
    }
    setStudentConditionsRowsLoading(false);
  };

  const getTchConds = async () => {
    setTeacherConditionsRowsLoading(true);
    const resultTchConds = await fetchGetTeacherConditionsAll();
    if (Object.keys(resultTchConds).includes("error")) {
      props.disconnect();
      props.setSnack((resultTchConds as any).error?.toString(), "error");
    } else {
      setTeacherConditionsRows(resultTchConds as TeacherConditionsNoNulls[]);
    }
    setTeacherConditionsRowsLoading(false);
  };

  const getFailed = async () => {
    const resultSortedFailed = await fetchGetFailed();
    if (Object.keys(resultSortedFailed).includes("error")) {
      props.disconnect();
      props.setSnack((resultSortedFailed as any).error?.toString(), "error");
    } else {
      setRowsFailed(resultSortedFailed as VFailedNoNulls[]);
    }
  };

  const refStuId = useRef(new Map<number, HTMLDivElement | null>());
  const refIgnoreNextStoredRestore = useRef(false);

  useEffect(() => {
    if (rowsLoading || studentConditionsRowsLoading || teacherConditionsRowsLoading || !props.targetId) return;
    refIgnoreNextStoredRestore.current = true;
    const el = refStuId.current.get(props.targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setSelectedRow(rows.find(s => s.id === props.targetId) ?? ({} as StudentsNoNulls));
    props.clearTarget();
  }, [rowsLoading, studentConditionsRowsLoading, teacherConditionsRowsLoading, props.targetId]);

  useEffect(() => {
    Promise.all([getStudents(), getStuConds(), getTchConds(), getFailed()]);
  }, []);

  useEffect(() => {
    if (refIgnoreNextStoredRestore.current) {
      refIgnoreNextStoredRestore.current = false;
      return;
    }
    const storedStu = rows.find(s => s.id === storedSelectedStudentId);
    if (!storedStu) return;
    const el = refStuId.current.get(storedStu.id);
    if (!el) return;
    el.scrollIntoView({ behavior: "instant", block: "center" });
    setSelectedRow(storedStu);
    setStoredSelectedStudentId(storedStu.id);
  }, [rows]);

  useMemo(() => {
    if (!selectedRow?.id) return;
    setSelectedRowStuConds(studentConditionsRows.filter(sc => sc.source_id === selectedRow.id));
    setSelectedRowTchConds(teacherConditionsRows.filter(tc => tc.source_id === selectedRow.id));
    setStoredSelectedStudentId(selectedRow.id);
  }, [studentConditionsRows, teacherConditionsRows, selectedRow]);

  const addStuCond = DoPostStuCond();
  const addTchCond = DoPostTchCond();
  const removeStuCondRow = DoDeleteStuCond();
  const removeTchCondRow = DoDeleteTchCond();
  const mutateStuCond = DoPutStuCond();
  const mutateTchCond = DoPutTchCond();

  const processStuCondRowRemove = useCallback(
    async (sc: StudentConditionsNoNulls) => {
      if (sc.owner_id === conditionOwnersDescToIdMap.get("SYSTEM")) {
        props.setSnack("Cannot edit SYSTEM rows", "warning");
        return;
      }
      setStudentConditionsRowsLoading(true);
      try {
        const res = (await removeStuCondRow(sc)) as Object;
        if (Object.keys(res).includes("error")) throw res;
        await getStuConds();
        props.setSnack("Student Condition successfully deleted", "success");
      } catch (err: any) {
        console.error(err);
        props.setSnack(err.error ?? err.toString(), "error");
        if (err.error === "401: Unauthorized") {
          props.disconnect();
        }
      } finally {
        setActionDeleteStuCondVisible(false);
        setStudentConditionsRowsLoading(false);
      }
    },
    [props, removeStuCondRow],
  );

  const processTchCondRowRemove = useCallback(
    async (tc: TeacherConditionsNoNulls) => {
      setTeacherConditionsRowsLoading(true);
      try {
        const res = (await removeTchCondRow(tc)) as Object;
        if (Object.keys(res).includes("error")) throw res;
        await getTchConds();
        props.setSnack("Teacher Condition successfully deleted", "success");
      } catch (err: any) {
        console.error(err);
        props.setSnack(err.error ?? err.toString(), "error");
      } finally {
        setActionDeleteTchCondVisible(false);
        setTeacherConditionsRowsLoading(false);
      }
    },
    [props, removeTchCondRow],
  );

  const handleProcessDataGridRowUpdateError = useCallback(
    (error: Error) => {
      props.setSnack(error.message, "error");
    },
    [props],
  );

  const processStuCondAdd = useCallback(
    async (sc: StudentConditionsNoNulls) => {
      setStudentConditionsRowsLoading(true);
      try {
        const res = await addStuCond(sc);
        if (Object.keys(res).includes("error")) throw res;
        const stuCondAdded = res as StudentConditionsNoNulls;
        await getStuConds();
        props.setSnack("Successfully added Student Condition", "success");
        return true;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err}`, "error");
        if (err.error === "401: Unauthorized") {
          props.disconnect();
        }
      } finally {
        setStudentConditionsRowsLoading(false);
      }
      return false;
    },
    [props, addStuCond, rows],
  );

  const processStuCondUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      if (oldRow.owner_id === conditionOwnersDescToIdMap.get("SYSTEM")) {
        props.setSnack("Cannot edit SYSTEM rows", "warning");
        return oldRow;
      }
      let diff = false;
      for (const k of Object.keys(newRow)) {
        if (newRow[k] !== oldRow[k]) {
          diff = true;
          break;
        }
      }
      if (!diff) {
        return oldRow;
      }

      setStudentConditionsRowsLoading(true);
      try {
        const res = await mutateStuCond(newRow as StudentConditionsNoNulls);
        props.setSnack("Student Condition successfully updated", "success");
        // setStudentConditionsRows(r => r.map(sc => (sc.id === res.id ? res : sc)));
        await getStuConds();
        return res;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err}`, "error");
        if (err.error === "401: Unauthorized") {
          props.disconnect();
        }
      } finally {
        setStudentConditionsRowsLoading(false);
      }
    },
    [props, mutateStuCond],
  );

  const processTchCondAdd = useCallback(
    async (tc: TeacherConditionsNoNulls) => {
      setTeacherConditionsRowsLoading(true);
      try {
        const res = await addTchCond(tc);
        if (Object.keys(res).includes("error")) throw res;
        const tchCondAdded = res as TeacherConditionsNoNulls;
        await getTchConds();
        props.setSnack("Successfully added Teacher Condition", "success");
        return true;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err}`, "error");
        if (err.error === "401: Unauthorized") {
          props.disconnect();
        }
      } finally {
        setTeacherConditionsRowsLoading(false);
      }
      return false;
    },
    [props, addTchCond, rows],
  );

  const processTchCondUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      let diff = false;
      for (const k of Object.keys(newRow)) {
        if (newRow[k] !== oldRow[k]) {
          diff = true;
          break;
        }
      }
      if (!diff) {
        return oldRow;
      }

      setTeacherConditionsRowsLoading(true);
      try {
        const res = await mutateTchCond(newRow as TeacherConditionsNoNulls);
        props.setSnack("Teacher Condition successfully updated", "success");
        // setTeacherConditionsRows(r => r.map(tc => (tc.id === res.id ? res : tc)));
        await getTchConds();
        return res;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err}`, "error");
        if (err.error === "401: Unauthorized") {
          props.disconnect();
        }
      } finally {
        setTeacherConditionsRowsLoading(false);
      }
    },
    [props, mutateTchCond],
  );

  const columnsStudentConditions: GridColDef[] = [
    {
      field: "actionDeleteStuCond",
      headerAlign: "center",
      headerName: "Delete",
      width: 70,
      minWidth: 70,
      align: "center",
      sortable: false,
      renderCell: params => (
        <Button
          startIcon={<DeleteIcon />}
          onClick={ev => processStuCondRowRemove(params.row as StudentConditionsNoNulls)}
        />
      ),
    },
    {
      field: "source_id",
      headerName: "From Student",
      valueFormatter: (val: number) => {
        const source = rows.find(r => r.id === val);
        return !source ? "" : `${source.last}, ${source.first}`;
      },
      width: 210,
    },
    {
      field: "definition_id",
      type: "singleSelect",
      valueOptions: CONDITION_DEFINITIONS.map(i => ({ value: i.id, label: i.desc })),
      headerName: "Definition",
      width: 110,
      editable: true,
    },
    {
      field: "target_id",
      headerName: "To Student",
      valueFormatter: (val: number) => {
        const target = rows.find(r => r.id === val);
        return !target ? "" : `${target.last}, ${target.first}`;
      },
      /**
       * edit: throws
       *   `Each child in a list should have a unique "key" prop`
       *   and "out-of-range value `[object Object]`"
       * anyway, so setting to uneditable for now
      type: "singleSelect",
      valueOptions: rows
        .filter(
          // todo: maybe abstract this out since it's also used for AddStudentConditionDialog `targets` prop
          tgt =>
            tgt.grade === selectedRow.grade &&
            !studentConditionsRows.map(sc => sc.targetId).includes(tgt._id) &&
            !studentConditionsRows.map(sc => sc.target?._id).includes(tgt._id) &&
            tgt._id !== selectedRow._id,
        )
        .map(i => ({ ...i, key: i._id })),
      */
      width: 210,
      editable: false, // todo: fix edit, so don't have to delete and re-add, then flip to `true`
    },
    {
      field: "owner_id",
      type: "singleSelect",
      valueOptions: CONDITION_OWNERS.filter(i => i.desc !== "SYSTEM").map(i => ({ value: i.id, label: i.desc })),
      headerName: "Says Who",
      width: 100,
      editable: true,
      sortable: false,
      valueFormatter: (val: number) => CONDITION_OWNERS.find(i => i.id === val)?.desc,
    },
    {
      field: "overruled",
      type: "boolean",
      headerName: "Overruled",
      width: 83,
      editable: true,
      sortable: false,
    },
    {
      field: "conflict",
      headerName: "Conflict",
      width: 80,
      sortable: false,
      renderCell: params => {
        const stuCond = params.row as StudentConditionsNoNulls;
        if (stuCond.owner_id === conditionOwnersDescToIdMap.get("SYSTEM")) return <></>;
        const fail = rowsFailed.find(f => f.s_id === stuCond.source_id && f.wstu_id);
        if (!fail) return <></>;
        return <PSTooltip child={<PSConflictIcon size={"medium"} />} title={fail.conf_desc} />;
      },
    },
  ];

  const columnsTeacherConditions: GridColDef[] = [
    {
      field: "actionDeleteTchCond",
      headerAlign: "center",
      headerName: "Delete",
      width: 70,
      minWidth: 70,
      align: "center",
      sortable: false,
      renderCell: params => (
        <Button
          startIcon={<DeleteIcon />}
          onClick={ev => processTchCondRowRemove(params.row as TeacherConditionsNoNulls)}
        />
      ),
    },
    {
      field: "source_id",
      headerName: "From Student",
      valueFormatter: (val: number) => {
        const source = rows.find(r => r.id === val);
        return !source ? "" : `${source.last}, ${source.first}`;
      },
      width: 210,
    },
    {
      field: "definition_id",
      type: "singleSelect",
      valueOptions: CONDITION_DEFINITIONS.map(i => ({ value: i.id, label: i.desc })),
      headerName: "Definition",
      width: 110,
      editable: true,
    },
    {
      field: "target_id",
      headerName: "To Teacher",
      valueFormatter: (val: number) => {
        const target = props.teachers.find(t => t.id === val);
        return !target ? "" : `${(target.prefix ?? "N/A") === "N/A" ? target.first : target.prefix} ${target.last}`;
      },
      width: 210,
      editable: false, // todo: see `columnsStudentConditions`'s field `target` before flipping to `true`
    },
    {
      field: "owner_id",
      type: "singleSelect",
      valueOptions: CONDITION_OWNERS.filter(i => i.desc !== "SYSTEM").map(i => ({ value: i.id, label: i.desc })),
      headerName: "Says Who",
      width: 100,
      editable: true,
      sortable: false,
    },
    {
      field: "overruled",
      type: "boolean",
      headerName: "Overruled",
      width: 83,
      editable: true,
      sortable: false,
    },
    {
      field: "conflict",
      headerName: "Conflict",
      width: 80,
      sortable: false,
      renderCell: params => {
        const tchCond = params.row as TeacherConditionsNoNulls;
        if (tchCond.owner_id === conditionOwnersDescToIdMap.get("SYSTEM")) return <></>;
        const fail = rowsFailed.find(f => f.s_id === tchCond.source_id && f.wtch_id);
        if (!fail) return <></>;
        return <PSTooltip child={<PSConflictIcon size={"medium"} />} title={fail.conf_desc} />;
      },
    },
  ];

  const handleListItemClick = (ev: MouseEvent, student: StudentsNoNulls) => {
    setSelectedRow(student);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        height: COMPONENT_CUSTOM_MAXHEIGHT(0),
        maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(0),
        overflow: "auto",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "flex-start",
          height: "100%",
          maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(4),
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: "1 1 0%",
            flexDirection: "column",
            height: "100%",
            maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(4),
            minWidth: 0,
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" component="div" color="text.secondary" sx={{ ml: 1 }}>
            Select Student
          </Typography>
          <Box sx={{ flex: "1", overflowY: "auto" }}>
            {rowsLoading ? <LinearProgress /> : <Box sx={{ minHeight: "4px" }}></Box>}
            <List sx={{ "& ul": { p: 0 } }} subheader={<li />}>
              {distinctGrades(rows).map(gr => (
                <li key={`grade-${gr}`}>
                  <ul>
                    <ListSubheader>{gr.replace("Gr", "Grade")}</ListSubheader>
                    {rows
                      .filter(s => s.grade === gr)
                      .map(stu => (
                        <ListItemButton
                          key={stu.id}
                          ref={el => {
                            refStuId.current.set(stu.id, el);
                          }}
                          selected={stu.id === selectedRow.id}
                          onClick={ev => handleListItemClick(ev, stu)}
                          sx={{
                            display: "flex",
                            px: 2,
                            py: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexGrow: 1,
                              alignItems: "center",
                              width: "100%",
                              maxWidth: "calc(100% - 75px)",
                            }}
                          >
                            <ListItemText
                              primary={`${stu.grade}: ${stu.last}, ${stu.first} (${stu.number})`}
                              slotProps={{
                                primary: {
                                  noWrap: true,
                                },
                              }}
                              sx={{ my: 0 }}
                            />
                          </Box>
                          <Box sx={{ width: 25, justifyContent: "center" }}>
                            {studentConditionsRows
                              .filter(i => i.owner_id !== conditionOwnersDescToIdMap.get("SYSTEM"))
                              .map(i => i.source_id)
                              .includes(stu.id) ? (
                              <GroupIcon fontSize="small" />
                            ) : (
                              <></>
                            )}
                          </Box>
                          <Box sx={{ width: 25, justifyContent: "center" }}>
                            {studentConditionsRows
                              .filter(i => i.owner_id === conditionOwnersDescToIdMap.get("SYSTEM"))
                              .map(i => i.source_id)
                              .includes(stu.id) ? (
                              <ManageAccountsOutlinedIcon fontSize="small" />
                            ) : (
                              <></>
                            )}
                          </Box>
                          <Box sx={{ width: 25, justifyContent: "center" }}>
                            {teacherConditionsRows.map(i => i.source_id).includes(stu.id) ? (
                              <CoPresentIcon fontSize="small" />
                            ) : (
                              <></>
                            )}
                          </Box>
                        </ListItemButton>
                      ))}
                  </ul>
                </li>
              ))}
            </List>
          </Box>
        </Box>
        <Box sx={{ alignSelf: "flex-start", flex: "2 1 0%", maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(120), minWidth: 0 }}>
          <Stack>
            <Typography variant="h6" component="div" color="text.secondary" sx={{ p: 1 }}>
              Student Conditions
            </Typography>
            <Box sx={{ flex: 1, minHeight: "117.5px" }}>
              <DataGrid
                getRowClassName={params =>
                  (params.row as StudentConditionsNoNulls).owner_id === conditionOwnersDescToIdMap.get("SYSTEM")
                    ? "system-row"
                    : ""
                }
                columns={columnsStudentConditions}
                columnVisibilityModel={{ actionDeleteStuCond: actionDeleteStuCondVisible }}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter
                loading={studentConditionsRowsLoading}
                onProcessRowUpdateError={handleProcessDataGridRowUpdateError}
                processRowUpdate={processStuCondUpdate}
                rows={selectedRowStuConds.toSorted((a, b) => a.id - b.id)}
                slots={{
                  loadingOverlay: LinearProgress as any,
                  noRowsOverlay: PSNoRowsOverlayCompact,
                  toolbar: () => (
                    <GridToolbarContainer>
                      <ButtonTrashRow
                        {...{
                          disabled:
                            selectedRowStuConds.filter(sc => sc.owner_id !== conditionOwnersDescToIdMap.get("SYSTEM"))
                              .length < 1,
                          visible: actionDeleteStuCondVisible,
                          toggleVisibility: setActionDeleteStuCondVisible,
                        }}
                      />
                      <Button
                        startIcon={<AddIcon />}
                        disabled={!selectedRow?.id}
                        onClick={() => setAddStudentConditionDialogOpen(true)}
                      >
                        Add Student Condition
                      </Button>
                    </GridToolbarContainer>
                  ),
                }}
                sx={{
                  maxHeight: selectedRowStuConds.length === 0 ? "117.5px" : "auto",
                  "& .MuiDataGrid-overlayWrapperInner": {
                    maxHeight: selectedRowStuConds.length === 0 ? "117.5px" : "auto",
                  },
                }}
              />
            </Box>
            <Typography variant="h6" component="div" color="text.secondary" sx={{ mt: 2, p: 1 }}>
              Teacher Conditions
            </Typography>
            <Box sx={{ flex: 1, minHeight: "117.5px" }}>
              <DataGrid
                columns={columnsTeacherConditions}
                columnVisibilityModel={{ actionDeleteTchCond: actionDeleteTchCondVisible }}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter
                loading={teacherConditionsRowsLoading}
                onProcessRowUpdateError={handleProcessDataGridRowUpdateError}
                processRowUpdate={processTchCondUpdate}
                rows={selectedRowTchConds.toSorted((a, b) => a.id - b.id)}
                slots={{
                  loadingOverlay: LinearProgress as any,
                  noRowsOverlay: PSNoRowsOverlayCompact,
                  toolbar: () => (
                    <GridToolbarContainer>
                      <ButtonTrashRow
                        {...{
                          disabled: selectedRowTchConds.length < 1,
                          visible: actionDeleteTchCondVisible,
                          toggleVisibility: setActionDeleteTchCondVisible,
                        }}
                      />
                      <Button
                        startIcon={<AddIcon />}
                        disabled={!selectedRow?.id}
                        onClick={() => setAddTeacherConditionDialogOpen(true)}
                      >
                        Add Teacher Condition
                      </Button>
                    </GridToolbarContainer>
                  ),
                }}
                sx={{
                  maxHeight: selectedRowTchConds.length === 0 ? "117.5px" : "auto",
                  "& .MuiDataGrid-overlayWrapperInner": {
                    maxHeight: selectedRowTchConds.length === 0 ? "117.5px" : "auto",
                  },
                }}
              />
            </Box>
          </Stack>
        </Box>
        <AddStudentConditionDialog
          {...{
            open: addStudentConditionDialogOpen,
            setOpen: setAddStudentConditionDialogOpen,
            setSnack: props.setSnack,
            source: selectedRow,
            targets: rows.filter(
              tgt =>
                tgt.grade === selectedRow.grade &&
                !studentConditionsRows
                  .filter(sc => sc.source_id === selectedRow.id)
                  .flatMap(sc => sc.target_id)
                  .includes(tgt.id) &&
                tgt.id !== selectedRow.id,
            ),
            processStuCondAdd,
          }}
        />
        <AddTeacherConditionDialog
          {...{
            open: addTeacherConditionDialogOpen,
            setOpen: setAddTeacherConditionDialogOpen,
            setSnack: props.setSnack,
            source: selectedRow,
            targets: props.teachers.filter(
              tgt =>
                tgt[teacherGrToFieldName(selectedRow.grade)] === true &&
                !teacherConditionsRows
                  .filter(tc => tc.source_id === selectedRow.id)
                  .flatMap(tc => tc.target_id)
                  .includes(tgt.id),
            ),
            processTchCondAdd,
          }}
        />
      </Stack>
    </Box>
  );
};
